/**
 * lib/lumiere.ts — la lumière vivante (22/10, Zidan) : les reliefs et les creux
 * suivent l'inclinaison du téléphone, comme sous une lampe fixe.
 *
 * Les ombres néomorphiques de globals.css multiplient leurs décalages par
 * `--lx` / `--ly` (1 par défaut : les ombres dessinées). On les écrit sur
 * <html>, comme les autres préférences d'interface (lib/prefs.ts) : un seul
 * endroit, et toute l'appli suit sans re-rendre un composant.
 */

export type Lumiere = 'on' | 'off';

export const LUMIERE_KEY = 'ogryn:lumiere';

/** Allumée par défaut : c'est un test, on la coupe dans les Paramètres. */
export function readLumiere(): Lumiere {
  try { return localStorage.getItem(LUMIERE_KEY) === 'off' ? 'off' : 'on'; } catch { return 'on'; }
}

/** Écrit la préférence et prévient l'écoute (components/lumiere-vivante.tsx). */
export function saveLumiere(v: Lumiere) {
  try { localStorage.setItem(LUMIERE_KEY, v); } catch {}
  window.dispatchEvent(new Event(LUMIERE_KEY));
}

/** iPhone : l'accès au mouvement se demande, et seulement sur un geste. Ailleurs,
 *  rien à demander. */
export async function autoriserLumiere(): Promise<boolean> {
  const D = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
  if (typeof D.requestPermission !== 'function') return true;
  try { return (await D.requestPermission()) === 'granted'; } catch { return false; }
}

/** La posture de lecture : tenu à 45°, droit — là où les ombres sont celles dessinées. */
const REPOS_BETA = 45;
const borne = (v: number) => Math.min(2, Math.max(-1, v));

/**
 * Une lampe au plafond, en haut à gauche (d'où nos ombres vers le bas à
 * droite). L'écran qui s'en détourne la reçoit en rasant, les ombres
 * s'allongent ; tourné vers elle, il la reçoit de face, elles raccourcissent
 * puis passent de l'autre côté. 45° d'écart font un cran.
 * ponytail: portrait seulement ; en paysage gamma et beta s'échangent.
 */
export function lumiere(beta: number | null, gamma: number | null): { lx: number; ly: number } {
  return {
    lx: borne(1 + (gamma ?? 0) / 45),
    ly: borne(1 + ((beta ?? REPOS_BETA) - REPOS_BETA) / 45),
  };
}

/** Écoute l'inclinaison et pose `--lx` / `--ly`, lissés. Renvoie l'arrêt, qui
 *  rend les ombres dessinées. */
export function demarrerLumiere(): () => void {
  const racine = document.documentElement.style;
  let cible = { lx: 1, ly: 1 }, cour = { lx: 1, ly: 1 }, dernier = 0, raf = 0;
  const surOrientation = (e: DeviceOrientationEvent) => { cible = lumiere(e.beta, e.gamma); };
  const tic = (t: number) => {
    raf = requestAnimationFrame(tic); // s'endort tout seul quand l'appli passe en arrière-plan
    if (t - dernier < 66) return; // ~15 images/s : chaque pas redessine toutes les ombres
    dernier = t;
    const lx = cour.lx + (cible.lx - cour.lx) * 0.3, ly = cour.ly + (cible.ly - cour.ly) * 0.3;
    if (Math.abs(lx - cour.lx) < 0.005 && Math.abs(ly - cour.ly) < 0.005) return;
    cour = { lx, ly };
    racine.setProperty('--lx', lx.toFixed(3));
    racine.setProperty('--ly', ly.toFixed(3));
  };
  window.addEventListener('deviceorientation', surOrientation);
  raf = requestAnimationFrame(tic);
  return () => {
    window.removeEventListener('deviceorientation', surOrientation);
    cancelAnimationFrame(raf);
    racine.removeProperty('--lx');
    racine.removeProperty('--ly');
  };
}
